import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, InputNumber, Select, TreeSelect, Tag, Space, message, Alert,
  Statistic, Row, Col, Descriptions, Progress, Tooltip, Popconfirm, Tabs
} from 'antd';
import { PlusOutlined, EyeOutlined, PauseCircleOutlined, PlayCircleOutlined, StopOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import { autoTradeApi, groupApi, tradingApi } from '../services/api';

const STATUS_COLOR = {
  buying: 'processing', running: 'processing', paused: 'warning',
  completed: 'success', stopped: 'default',
};
const STATUS_LABEL = {
  pending_buy: '待买入', buying: '买入中', running: '监控中', paused: '已暂停',
  completed: '已完成', stopped: '已停止',
};
const BUY_COLOR = { pending_buy: 'default', sending: 'processing', bought: 'success', failed: 'error' };
const BUY_LABEL = { pending_buy: '待买入', sending: '买入中', bought: '已买入', failed: '买入失败' };
const SELL_COLOR = { pending: 'default', sending: 'processing', sold: 'success', failed: 'error' };
const SELL_LABEL = { pending: '待卖出', sending: '卖出中', sold: '已卖出', failed: '卖出失败' };

export default function AutoTradePage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTask, setDetailTask] = useState(null);
  const [details, setDetails] = useState([]);
  const [groups, setGroups] = useState([]);

  // 分组树形选项：顶层分组（parent_id 为空）为一级，子分组作为其 children 缩进显示
  const groupTreeOptions = useMemo(() => {
    const map = {};
    const roots = [];
    groups.forEach(g => { map[g.id] = { ...g, children: undefined }; });
    groups.forEach(g => {
      if (g.parent_id && map[g.parent_id]) {
        const p = map[g.parent_id];
        (p.children || (p.children = [])).push(map[g.id]);
      } else {
        roots.push(map[g.id]);
      }
    });
    const toOptions = (nodes) => nodes.map(g => ({
      value: g.id,
      title: g.name,
      ...(g.children && g.children.length ? { children: toOptions(g.children) } : {}),
    }));
    return toOptions(roots);
  }, [groups]);
  const [form] = Form.useForm();
  const [preview, setPreview] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const previewTimer = useRef(null);

  const loadTasks = () => {
    setLoading(true);
    autoTradeApi.list().then(r => {
      if (r.success) setTasks(r.data || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadTasks(); groupApi.list().then(r => { if (r.success) setGroups(r.data || []); }); }, []);

  // 实时预览符合资金范围的客户
  const doPreview = (groupId, fundMin, fundMax) => {
    if (!groupId) { setPreview([]); return; }
    setPreviewLoading(true);
    autoTradeApi.previewClients({ group_id: groupId, fund_min: fundMin || 0, fund_max: fundMax || 0 })
      .then(r => { if (r.success) setPreview(r.data || []); })
      .finally(() => setPreviewLoading(false));
  };
  useEffect(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
  }, []);

  // 输入股票代码后自动加载行情（名称 + 最新价）
  const fetchQuoteInfo = (code) => {
    tradingApi.getQuote(code).then(r => {
      if (r?.success && r.data) {
        form.setFieldValue('stock_name', r.data.stock_name || '');
        setQuoteInfo({ name: r.data.stock_name, price: r.data.current_price });
        setLastUpdate(new Date());
      }
    }).catch(() => {});
  };

  // 表单字段变化（股票代码 / 分组 / 资金范围）时联动
  const onFormValuesChange = (changed, all) => {
    if (!createOpen) return;
    // 股票代码变化：自动加载名称与最新价
    if ('stock_code' in changed) {
      const code = (all.stock_code || '').trim();
      setStockCode(code);
      if (code.length >= 6) {
        fetchQuoteInfo(code);
      } else {
        form.setFieldValue('stock_name', '');
        setQuoteInfo(null);
      }
    }
    // 盈利比例变化：同步用于预估计算
    if ('profit_rate' in changed) {
      setProfitRate(all.profit_rate != null ? all.profit_rate : 0);
    }
    // 指定买入价变化：同步用于预估计算
    if ('buy_target_price' in changed) {
      setBuyTargetPrice(all.buy_target_price != null ? all.buy_target_price : 0);
    }
    if (!('group_id' in changed) && !('fund_min' in changed) && !('fund_max' in changed)) return;
    // 分组切换：立即列出客户；资金范围变化：防抖刷新
    if ('group_id' in changed) {
      doPreview(all.group_id, all.fund_min, all.fund_max);
    } else {
      if (previewTimer.current) clearTimeout(previewTimer.current);
      previewTimer.current = setTimeout(() => doPreview(all.group_id, all.fund_min, all.fund_max), 300);
    }
  };

  // 手动刷新可用资金（重新拉取分组客户及其余额）
  const refreshPreview = () => {
    const v = form.getFieldsValue();
    doPreview(v.group_id, v.fund_min, v.fund_max);
  };

  const [profitRate, setProfitRate] = useState(2); // 盈利比例（%）
  const [quoteInfo, setQuoteInfo] = useState(null); // 输入股票代码后自动加载的名称与最新价
  const [buyTargetPrice, setBuyTargetPrice] = useState(0); // 指定买入价（监控触发价）
  const [buyMode, setBuyMode] = useState('current'); // 买入价来源：current=当前价 | custom=指定买入价
  const [stockCode, setStockCode] = useState(''); // 当前输入的股票代码（用于轮询刷新行情）
  const [lastUpdate, setLastUpdate] = useState(null); // 行情最后刷新时刻

  // 新增任务弹窗内：股票行情每 3 秒刷新一次（当前价 / 预计卖出价 / 指定买入价判断实时联动）
  const quoteTimerRef = useRef(null);
  useEffect(() => {
    if (quoteTimerRef.current) { clearInterval(quoteTimerRef.current); quoteTimerRef.current = null; }
    if (createOpen && /^\d{6}$/.test(stockCode)) {
      quoteTimerRef.current = setInterval(() => { fetchQuoteInfo(stockCode); }, 3000);
    }
    return () => {
      if (quoteTimerRef.current) { clearInterval(quoteTimerRef.current); quoteTimerRef.current = null; }
    };
  }, [createOpen, stockCode]);

  // 根据可用资金、买入价（tab 选择的当前价或指定买入价）、盈利比例，预估每客户买入金额/股数/盈利及总盈利
  const previewCalc = useMemo(() => {
    const buyPrice = buyMode === 'custom' && buyTargetPrice > 0 ? buyTargetPrice : quoteInfo?.price;
    let totalProfit = 0;
    const rows = preview.map(p => {
      const fund = p.available_fund || 0;
      let shares = 0;
      if (buyPrice && buyPrice > 0) {
        shares = Math.floor(fund / (buyPrice * 100)) * 100; // A股整百股
      }
      const buyAmount = shares * buyPrice;
      const profit = buyAmount * (profitRate / 100);
      totalProfit += profit;
      return { ...p, buyPrice, estShares: shares, estBuyAmount: buyAmount, estProfit: profit };
    });
    return { rows, totalProfit };
  }, [preview, quoteInfo, profitRate, buyTargetPrice, buyMode]);

  const openCreate = () => {
    form.resetFields();
    form.setFieldsValue({ fund_min: 10000, fund_max: 100000, profit_rate: 2 });
    setPreview([]);
    setQuoteInfo(null);
    setCreateOpen(true);
    // 不自动选择分组：由用户手动选择分组后，自动列出符合资金范围的客户
  };

  const onSubmit = async () => {
    const v = await form.validateFields();
    if (!v.group_id) { message.error('请选择分组'); return; }
    if (preview.length === 0) { message.error('当前资金范围内没有符合的客户'); return; }
    setSubmitting(true);
    // 卖出价：仅当用户显式填写才传入；否则传 0，运行时按实际买入价×(1+盈利比例)动态计算（锚定真实成本）
    // 买入价来源：选「当前价」则不传指定买入价（后端按当前价立即买）；选「指定买入价」才传
    const buyTarget = buyMode === 'custom' ? (v.buy_target_price || 0) : 0;
    const res = await autoTradeApi.create({
      name: v.name, stock_code: v.stock_code, stock_name: v.stock_name,
      profit_rate: v.profit_rate / 100, sell_price: 0, group_id: v.group_id,
      buy_target_price: buyTarget,
      fund_min: v.fund_min || 0, fund_max: v.fund_max || 0,
    });
    setSubmitting(false);
    if (res.success) { message.success('任务已创建，正在买入'); setCreateOpen(false); loadTasks(); }
    else message.error(res.message || '创建失败');
  };

  const openDetail = (task) => {
    setDetailTask(task); setDetailOpen(true);
    autoTradeApi.getDetails(task.id).then(r => { if (r.success) setDetails(r.data || []); });
  };
  useEffect(() => {
    if (!detailOpen || !detailTask) return;
    const t = setInterval(() => {
      autoTradeApi.getDetails(detailTask.id).then(r => { if (r.success) setDetails(r.data || []); });
    }, 6000);
    return () => clearInterval(t);
  }, [detailOpen, detailTask]);

  const setStatus = async (task, action) => {
    const r = await autoTradeApi.setStatus(task.id, action);
    if (r.success) { message.success('已更新'); loadTasks(); } else message.error(r.message || '操作失败');
  };

  const delTask = async (task) => {
    const r = await autoTradeApi.delete(task.id);
    if (r.success) { message.success('已删除'); loadTasks(); } else message.error(r.message || '删除失败');
  };

  const totalBought = tasks.reduce((a, t) => a + t.bought_count, 0);
  const totalSold = tasks.reduce((a, t) => a + t.sold_count, 0);
  const totalBuyFail = tasks.reduce((a, t) => a + t.buy_fail_count, 0);
  const totalSellFail = tasks.reduce((a, t) => a + t.sell_fail_count, 0);

  const taskColumns = [
    { title: '任务名称', dataIndex: 'name', key: 'name' },
    { title: '股票 / 最新价', key: 'stock', render: (_, r) => (
      <span>
        {r.stock_code} {r.stock_name}
        {r.current_price != null && r.current_price > 0
          ? <span style={{ color: '#cf1322', fontWeight: 600 }}>　¥{r.current_price.toFixed(2)}</span>
          : null}
      </span>
    ) },
    { title: '分组', dataIndex: 'group_name', key: 'group_name' },
    { title: '盈利%', key: 'profit', render: (_, r) => (r.profit_rate * 100).toFixed(1) + '%' },
    { title: '买入价', dataIndex: 'buy_price', key: 'buy_price', render: v => v ? v.toFixed(2) : '-' },
    { title: '卖出价', dataIndex: 'sell_price', key: 'sell_price', render: v => v ? v.toFixed(2) : '-' },
    { title: '卖出监控开始', dataIndex: 'sell_monitor_start', key: 'sell_monitor_start', render: v => v ? new Date(v).toLocaleString() : '-' },
    { title: '资金范围', key: 'fund', render: (_, r) => `${r.fund_min || 0} ~ ${r.fund_max || '∞'}` },
    { title: '客户/已买/已卖', key: 'prog', render: (_, r) =>
      `${r.detail_count} / ${r.bought_count} / ${r.sold_count}` + (r.buy_fail_count ? ` (买失败${r.buy_fail_count})` : '') },
    { title: '当前价', dataIndex: 'current_price', key: 'current_price', render: v => v ? v.toFixed(2) : '-' },
    { title: '状态', dataIndex: 'status', key: 'status', render: s => <Tag color={STATUS_COLOR[s]}>{STATUS_LABEL[s] || s}</Tag> },
    { title: '操作', key: 'op', render: (_, r) => (
      <Space>
        <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(r)}>明细</Button>
        {r.status === 'running' && <Button size="small" icon={<PauseCircleOutlined />} onClick={() => setStatus(r, 'pause')}>暂停</Button>}
        {r.status === 'paused' && <Button size="small" icon={<PlayCircleOutlined />} onClick={() => setStatus(r, 'resume')}>恢复</Button>}
        {(r.status === 'running' || r.status === 'paused' || r.status === 'buying') &&
          <Button size="small" danger icon={<StopOutlined />} onClick={() => setStatus(r, 'stop')}>停止</Button>}
        {r.status !== 'running' && r.status !== 'buying' && (
          <Popconfirm title="确认删除该任务？" description="将同时删除其所有客户明细" okText="删除" cancelText="取消"
            onConfirm={() => delTask(r)}>
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        )}
      </Space>
    ) },
  ];

  const detailColumns = [
    { title: '客户', dataIndex: 'client_name', key: 'client_name' },
    { title: '账户', dataIndex: 'account_id', key: 'account_id' },
    { title: '可用资金', dataIndex: 'available_fund', key: 'available_fund', render: v => v ? v.toFixed(2) : '-' },
    { title: '买入', key: 'buy', render: (_, r) => <Tag color={BUY_COLOR[r.buy_status]}>{BUY_LABEL[r.buy_status]}</Tag> },
    { title: '买入指令时间', dataIndex: 'buy_sent_at', key: 'buy_sent_at', render: v => v ? new Date(v).toLocaleString() : '-' },
    { title: '买入量', dataIndex: 'buy_qty', key: 'buy_qty' },
    { title: '买入价', dataIndex: 'buy_price', key: 'buy_price', render: v => v ? v.toFixed(2) : '-' },
    { title: '买入额', dataIndex: 'buy_amount', key: 'buy_amount', render: v => v ? v.toFixed(2) : '-' },
    { title: '当前价', dataIndex: 'current_price', key: 'current_price', render: v => v ? v.toFixed(2) : '-' },
    { title: '市值', dataIndex: 'market_value', key: 'market_value', render: v => v ? v.toFixed(2) : '-' },
    { title: '卖出触发价', dataIndex: 'sell_target_price', key: 'sell_target_price', render: v => v ? v.toFixed(2) : '-' },
    { title: '预计盈利', key: 'est_profit', render: (_, r) => {
        if (r.buy_price > 0 && r.buy_qty > 0 && r.sell_target_price > 0) {
          const p = (r.sell_target_price - r.buy_price) * r.buy_qty;
          return <span style={{ color: '#cf1322', fontWeight: 600 }}>{p.toFixed(2)}</span>;
        }
        return '-';
      } },
    { title: '卖出', key: 'sell', render: (_, r) => <Tag color={SELL_COLOR[r.sell_status]}>{SELL_LABEL[r.sell_status]}</Tag> },
    { title: '卖出量', dataIndex: 'sell_qty', key: 'sell_qty' },
    { title: '卖出额', dataIndex: 'sell_amount', key: 'sell_amount', render: v => v ? v.toFixed(2) : '-' },
    { title: '备注', key: 'msg', render: (_, r) => <Tooltip title={r.sell_msg || r.buy_msg}><span style={{ color: '#999' }}>{r.sell_msg || r.buy_msg || '-'}</span></Tooltip> },
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}><Card><Statistic title="任务总数" value={tasks.length} /></Card></Col>
        <Col span={6}><Card><Statistic title="已买入客户" value={totalBought} /></Card></Col>
        <Col span={6}><Card><Statistic title="已卖出客户" value={totalSold} /></Card></Col>
        <Col span={6}><Card><Statistic title="失败(买/卖)" value={`${totalBuyFail}/${totalSellFail}`} /></Card></Col>
      </Row>

      <Card title="自动交易任务"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadTasks}>刷新</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增自动交易任务</Button>
          </Space>
        }>
        <Table rowKey="id" loading={loading} columns={taskColumns} dataSource={tasks} pagination={{ pageSize: 10 }} />
      </Card>

      {/* 新增任务弹窗 */}
      <Modal title="新增自动交易任务" open={createOpen} onOk={onSubmit} onCancel={() => setCreateOpen(false)}
        confirmLoading={submitting} okText="创建并买入" cancelText="取消" width={760} destroyOnClose>
        <Form form={form} layout="vertical" onValuesChange={onFormValuesChange}>
          <Form.Item name="stock_code" label="股票代码" rules={[{ required: true, message: '请输入股票代码' }]}>
            <Input placeholder="输入代码自动加载名称与最新价，如 600519" />
          </Form.Item>
          {/* 名称由代码自动加载，无需手动输入 */}
          <Form.Item name="stock_name" hidden initialValue="">
            <Input />
          </Form.Item>
          {quoteInfo && (
            <div style={{ marginTop: -12, marginBottom: 12, color: '#1890ff', fontSize: 13 }}>
              已识别：{quoteInfo.name || '未知'}　最新价：<b>{quoteInfo.price != null ? quoteInfo.price : '-'}</b>
              <span style={{ color: '#52c41a' }}>　●实时刷新(3s)</span>
              {lastUpdate && <span style={{ color: '#999' }}>　更新于 {lastUpdate.toLocaleTimeString()}</span>}
            </div>
          )}
          <Form.Item label="买入方式" tooltip="按市二价（卖二价）自动买入">
            <Tag color="blue" style={{ fontSize: 14 }}>市二价买入</Tag>
          </Form.Item>

          {/* 买入价来源：当前价 / 指定买入价 tab 切换 */}
          <Tabs activeKey={buyMode} onChange={(k) => {
            setBuyMode(k);
            if (k === 'current') { setBuyTargetPrice(0); form.setFieldValue('buy_target_price', undefined); }
          }} items={[
            {
              key: 'current', label: '当前价',
              children: (
                <div style={{ color: '#1890ff', fontSize: 14, padding: '4px 0 8px' }}>
                  以当前最新价买入：
                  <b>{quoteInfo?.price != null ? quoteInfo.price : '-'}</b>
                  {quoteInfo?.name ? `（${quoteInfo.name}）` : ''}
                </div>
              )
            },
            {
              key: 'custom', label: '指定买入价',
              children: (
                <Form.Item name="buy_target_price" label="指定买入价（回调到该价才买入）" tooltip="监控股价，到达该价（≤）后按卖二价买入"
                  rules={[{
                    validator: (_, val) => {
                      if (val != null && val > 0 && quoteInfo?.price != null && val > quoteInfo.price) {
                        return Promise.reject(new Error(`买入价不能高于当前价 ${quoteInfo.price}`));
                      }
                      return Promise.resolve();
                    }
                  }]}>
                  <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="如 10.50（应 ≤ 当前价）" />
                </Form.Item>
              )
            }
          ]} />

          {/* 盈利比例：放到下面 */}
          <Form.Item name="profit_rate" label="盈利比例 (%)" rules={[{ required: true, message: '请输入盈利比例' }]}>
            <InputNumber min={0.1} max={100} step={0.5} style={{ width: '100%' }} addonAfter="%" placeholder="如 5 表示赚 5% 卖出" />
          </Form.Item>

          {/* 预计卖出价：按所选买入价 × (1+盈利比例) 计算 */}
          {(() => {
            const base = buyMode === 'custom' && buyTargetPrice > 0 ? buyTargetPrice : quoteInfo?.price;
            if (base == null || base <= 0 || !profitRate) return null;
            const sell = base * (1 + profitRate / 100);
            const tag = buyMode === 'custom' && buyTargetPrice > 0 ? '指定买入价' : '当前价';
            return (
              <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, fontSize: 13 }}>
                预计卖出价：<b style={{ color: '#fa8c16', fontSize: 15 }}>{sell.toFixed(2)}</b>
                <span style={{ color: '#999' }}>　= {tag} {base} × (1 + {profitRate}%)</span>
              </div>
            );
          })()}

          <Form.Item name="group_id" label="选择分组" rules={[{ required: true, message: '请选择分组' }]}>
            <TreeSelect
              placeholder="选择客户分组"
              treeData={groupTreeOptions}
              treeDefaultExpandAll
              showSearch
              treeNodeFilterProp="title"
              allowClear
              treeLine
            />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="fund_min" label="可用资金下限">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="0 表示不限" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="fund_max" label="可用资金上限">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="0 表示不限" />
              </Form.Item>
            </Col>
          </Row>
          <Card size="small"
            title={`符合资金范围的客户（${preview.length}）`}
            loading={previewLoading}
            extra={<Button size="small" onClick={refreshPreview} disabled={!form.getFieldValue('group_id')}>刷新可用资金</Button>}>
            {preview.length > 0 && (
              <Alert
                type="success"
                showIcon
                style={{ marginBottom: 8 }}
                message={<span>预估总盈利：<b style={{ fontSize: 14 }}>{previewCalc.totalProfit.toFixed(2)}</b> 元（按当前价 {quoteInfo?.price != null ? quoteInfo.price : '-'} × 盈利 {profitRate}% 估算，{preview.length} 户）</span>}
              />
            )}
            <Table rowKey="client_id" size="small"
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'], size: 'small' }}
              columns={[
                { title: '客户', dataIndex: 'client_name' },
                { title: '账户', dataIndex: 'account_id' },
                { title: '可用资金', dataIndex: 'available_fund', render: v => v ? v.toFixed(2) : '-' },
                { title: '预计买入金额', dataIndex: 'estBuyAmount', render: (v, r) => r.buyPrice ? v.toFixed(2) : '-' },
                { title: '预计买入股数', dataIndex: 'estShares', render: (v, r) => r.buyPrice ? v : '-' },
                { title: '盈利比例', dataIndex: 'profitRate', render: () => `${profitRate}%` },
                { title: '预计盈利', dataIndex: 'estProfit', render: (v, r) => r.buyPrice ? v.toFixed(2) : '-' },
              ]}
              dataSource={previewCalc.rows} locale={{ emptyText: '请选择分组并设置资金范围' }} />
          </Card>
        </Form>
      </Modal>

      {/* 明细弹窗 */}
      <Modal title={`任务明细 - ${detailTask?.name || ''}`} open={detailOpen} onCancel={() => setDetailOpen(false)}
        footer={null} width={1300} destroyOnClose>
        {detailTask && (
          <Descriptions size="small" column={4} bordered style={{ marginBottom: 12 }}>
            <Descriptions.Item label="股票">{detailTask.stock_code} {detailTask.stock_name}</Descriptions.Item>
            <Descriptions.Item label="盈利%">{(detailTask.profit_rate * 100).toFixed(1)}%</Descriptions.Item>
            <Descriptions.Item label="卖出价">{detailTask.sell_price ? detailTask.sell_price.toFixed(2) : '盈利比例自动'}</Descriptions.Item>
            <Descriptions.Item label="指定买入价">{detailTask.buy_target_price ? detailTask.buy_target_price.toFixed(2) : '实时价买入'}</Descriptions.Item>
            <Descriptions.Item label="卖出监控开始">{detailTask.sell_monitor_start ? new Date(detailTask.sell_monitor_start).toLocaleString() : '-'}</Descriptions.Item>
            <Descriptions.Item label="当前价">{detailTask.current_price ? detailTask.current_price.toFixed(2) : '-'}</Descriptions.Item>
            <Descriptions.Item label="状态"><Tag color={STATUS_COLOR[detailTask.status]}>{STATUS_LABEL[detailTask.status]}</Tag></Descriptions.Item>
            <Descriptions.Item label="买入进度">{detailTask.bought_count}/{detailTask.detail_count}</Descriptions.Item>
            <Descriptions.Item label="卖出进度">{detailTask.sold_count}/{detailTask.bought_count}</Descriptions.Item>
            <Descriptions.Item label="资金范围">{detailTask.fund_min || 0} ~ {detailTask.fund_max || '∞'}</Descriptions.Item>
            <Descriptions.Item label="分组">{detailTask.group_name}</Descriptions.Item>
          </Descriptions>
        )}
        <Table rowKey="id" size="small" columns={detailColumns} dataSource={details}
          scroll={{ x: 1500 }} pagination={{ pageSize: 20 }} />
      </Modal>
    </div>
  );
}
